import { useEffect, useRef, useState } from "react";
import { shortUuid } from "@/utils/deviceName";

type MapControlsProps = {
  map: google.maps.Map | null;
  floors: number[];
  selectedFloor: number | "all";
  onFloorChange: (floor: number | "all") => void;
  onSetDefaultCenter: () => void;
  devices: { uuid: string; label: string }[];
  followedUuid: string | null;
  onFollowChange: (uuid: string | null) => void;
};

/**
 * Floating overlay for controlling the map view: free-text place search (Google Places
 * Autocomplete, new `PlaceAutocompleteElement` widget), manual lat/lng centering, and a
 * floor picker for multi-floor sites.
 */
export function MapControls({
  map,
  floors,
  selectedFloor,
  onFloorChange,
  onSetDefaultCenter,
  devices,
  followedUuid,
  onFollowChange,
}: MapControlsProps) {
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(
    null,
  );
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mount the Places Autocomplete widget into the search container once the map and script are ready
  useEffect(() => {
    if (!map || !searchContainerRef.current || autocompleteRef.current) return;
    if (!window.google?.maps?.places?.PlaceAutocompleteElement) return;

    const autocomplete = new google.maps.places.PlaceAutocompleteElement({
      locationBias: map.getBounds() ?? undefined,
    });
    autocomplete.className = "w-full border border-gray-300 rounded";
    autocomplete.setAttribute("placeholder", "Search for a location...");
    // Element otherwise follows OS/browser color-scheme (renders dark on dark-mode systems),
    // clashing with this widget's light card background
    autocomplete.style.colorScheme = "light";
    searchContainerRef.current.appendChild(autocomplete);

    // `gmp-select` is only typed loosely in @types/google.maps (beta-channel API)
    autocomplete.addEventListener("gmp-select", async (event) => {
      const { placePrediction } = event as unknown as {
        placePrediction: google.maps.places.PlacePrediction;
      };
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ["location"] });
      if (!place.location) return;

      map.panTo(place.location);
      map.setZoom(18);
    });

    autocompleteRef.current = autocomplete;
  }, [map]);

  const handleGoToCoordinates = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng) || !map) return;

    map.panTo({ lat: parsedLat, lng: parsedLng });
    map.setZoom(18);
  };

  const handleSetDefaultCenter = () => {
    onSetDefaultCenter();

    setJustSaved(true);
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => setJustSaved(false), 1500);
  };

  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-2 bg-white/95 rounded-md shadow-md p-3 text-sm w-64">
      <div ref={searchContainerRef} className="w-full" />

      <form onSubmit={handleGoToCoordinates} className="flex gap-1">
        <input
          type="text"
          inputMode="decimal"
          placeholder="Lat"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 w-1/3"
        />
        <input
          type="text"
          inputMode="decimal"
          placeholder="Lng"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 w-1/3"
        />
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white rounded px-2 py-1 w-1/3"
        >
          Go
        </button>
      </form>

      <button
        type="button"
        onClick={handleSetDefaultCenter}
        disabled={!map}
        title="Remember the current map view as the default center for this session"
        className={`rounded px-2 py-1 text-xs transition-colors duration-700 disabled:opacity-50 ${
          justSaved
            ? "bg-green-500 text-white"
            : "bg-gray-200 hover:bg-gray-300 text-gray-800"
        }`}
      >
        {justSaved ? "Saved!" : "Set as default location"}
      </button>

      {(devices.length > 0 || followedUuid) && (
        <label className="flex flex-col gap-1">
          <span className="text-gray-600">Follow device</span>
          <select
            className="border border-gray-300 rounded px-2 py-1"
            value={followedUuid ?? ""}
            onChange={(e) => onFollowChange(e.target.value || null)}
          >
            <option value="">Off</option>
            {/* keep the followed device selectable even if it stops reporting */}
            {(followedUuid && !devices.some((d) => d.uuid === followedUuid)
              ? [{ uuid: followedUuid, label: shortUuid(followedUuid) }, ...devices]
              : devices
            ).map(({ uuid, label }) => (
              <option key={uuid} value={uuid} title={uuid}>
                {label}
              </option>
            ))}
          </select>
        </label>
      )}

      {floors.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="text-gray-600">
            Floor{followedUuid ? " (following device)" : ""}
          </span>
          <select
            className="border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
            value={selectedFloor}
            disabled={!!followedUuid}
            onChange={(e) =>
              onFloorChange(
                e.target.value === "all" ? "all" : Number(e.target.value),
              )
            }
          >
            <option value="all">All Floors</option>
            {floors.map((floor) => (
              <option key={floor} value={floor}>
                Floor {floor}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
